import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Chip, Tabs, Tab, MenuItem, IconButton, Alert, Paper,
  alpha, useTheme, Switch, FormControlLabel,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import HubServiceTableCell from '../../components/services/HubServiceTableCell';
import { Add, Edit, Delete } from '@mui/icons-material';
import {
  createPlatformService, deletePlatformService, getPlatformServices,
  updatePlatformService, type PlatformServiceDef,
} from '../../services/platform/platformServiceService';
import { useServiceCategories } from '../../hooks/useServiceCatalog';

const ICON_OPTIONS = [
  'DirectionsCar', 'Restaurant', 'ShoppingCart', 'Build', 'Event', 'Flight',
  'Hotel', 'DirectionsBus', 'Train', 'CarRental', 'LocalPharmacy', 'MedicalServices',
  'AccountBalanceWallet', 'Stars', 'Subscriptions', 'Contacts', 'SupportAgent', 'Badge', 'Store',
];

const emptyService = (): Partial<PlatformServiceDef> => ({
  code: '', name: '', description: '', category: 'transportation', icon: 'Build',
  primaryColor: '#1976d2', secondaryColor: '#dc004e', serviceType: 'business', status: 'active',
  supportedCountries: [], masterGroupKeys: [], menuItems: [], mobileMenuItems: [],
  subServiceTemplates: [], settingCategories: [], featureKeys: [], sortOrder: 100,
});

export default function ServiceManagementPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState<PlatformServiceDef | null>(null);
  const [form, setForm] = useState<Partial<PlatformServiceDef>>(emptyService());
  const [menuDraft, setMenuDraft] = useState({ label: '', path: '', icon: 'Build', sortOrder: 0 });
  const [subDraft, setSubDraft] = useState({ name: '', slug: '' });

  const { data: services = [], isLoading } = useQuery({
    queryKey: ['platform-services-all'],
    queryFn: () => getPlatformServices(),
  });
  const { data: categories = [] } = useServiceCategories();

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<PlatformServiceDef>) =>
      editing ? updatePlatformService(editing.code, payload) : createPlatformService(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-services-all'] });
      queryClient.invalidateQueries({ queryKey: ['platform-services'] });
      queryClient.invalidateQueries({ queryKey: ['service-catalog'] });
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyService());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => deletePlatformService(code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-services-all'] }),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, PlatformServiceDef[]>();
    for (const svc of services) {
      const list = map.get(svc.category) || [];
      list.push(svc);
      map.set(svc.category, list);
    }
    return map;
  }, [services]);

  const columns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 120 },
    {
      field: 'name',
      headerName: 'Service',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <HubServiceTableCell
          service={{
            name: row.name as string,
            icon: row.icon as string,
            themeColor: row.primaryColor as string,
          }}
          accentColor={row.primaryColor as string}
        />
      ),
    },
    { field: 'category', headerName: 'Category', width: 140 },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (p) => (
        <Chip label={p.value as string} size="small"
          color={p.value === 'active' ? 'success' : p.value === 'draft' ? 'warning' : 'default'} />
      ),
    },
    {
      field: 'serviceType', headerName: 'Type', width: 100,
      renderCell: (p) => <Chip label={p.value as string} size="small" variant="outlined" />,
    },
    {
      field: 'subServiceTemplates', headerName: 'Sub-Services', width: 120,
      valueGetter: (v) => (v as unknown[])?.length || 0,
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={() => {
            setEditing(p.row as PlatformServiceDef);
            setForm(p.row as PlatformServiceDef);
            setDialogOpen(true);
          }}><Edit fontSize="small" /></IconButton>
          {!(p.row as PlatformServiceDef).isSystem && (
            <IconButton size="small" color="error" onClick={() => deleteMutation.mutate((p.row as PlatformServiceDef).code)}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const setField = <K extends keyof PlatformServiceDef>(key: K, value: PlatformServiceDef[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Service Management Engine</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and configure platform services — no code changes required
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => {
          setEditing(null);
          setForm(emptyService());
          setTab(0);
          setDialogOpen(true);
        }}>New Service</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Services defined here drive tenant modules, navigation menus, master data visibility, and sub-service templates.
      </Alert>

      {[...grouped.entries()].map(([cat, items]) => (
        <Paper key={cat} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
            {categories.find((c) => c.code === cat)?.name || cat}
          </Typography>
          <SerialNoDataGrid rows={items} columns={columns} getRowId={(r) => r.code} autoHeight hideFooter
            loading={isLoading} disableRowSelectionOnClick
            sx={{ border: 'none', '& .MuiDataGrid-columnHeaders': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }} />
        </Paper>
      ))}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'Create Service'}</DialogTitle>
        <DialogContent dividers>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="General" /><Tab label="Branding" /><Tab label="Menus" /><Tab label="Sub-Services" />
          </Tabs>

          {tab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Service Code" value={form.code || ''}
                  disabled={!!editing} onChange={(e) => setField('code', e.target.value.toLowerCase().replace(/\s+/g, '_'))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Service Name" value={form.name || ''}
                  onChange={(e) => setField('name', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Description" value={form.description || ''}
                  onChange={(e) => setField('description', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth size="small" label="Category" value={form.category || ''}
                  onChange={(e) => setField('category', e.target.value)}>
                  {categories.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth size="small" label="Type" value={form.serviceType || 'business'}
                  onChange={(e) => setField('serviceType', e.target.value as PlatformServiceDef['serviceType'])}>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="platform">Platform</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth size="small" label="Status" value={form.status || 'active'}
                  onChange={(e) => setField('status', e.target.value as PlatformServiceDef['status'])}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="deprecated">Deprecated</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth size="small" label="Icon" value={form.icon || 'Build'}
                  onChange={(e) => setField('icon', e.target.value)}>
                  {ICON_OPTIONS.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="number" label="Sort Order" value={form.sortOrder ?? 0}
                  onChange={(e) => setField('sortOrder', Number(e.target.value))} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Countries (comma-separated)" placeholder="IN,US,UAE"
                  value={(form.supportedCountries || []).join(',')}
                  onChange={(e) => setField('supportedCountries', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={!!form.countryRequired} onChange={(e) => setField('countryRequired', e.target.checked)} />} label="Country Required" />
                <FormControlLabel control={<Switch checked={!!form.stateRequired} onChange={(e) => setField('stateRequired', e.target.checked)} />} label="State Required" />
                <FormControlLabel control={<Switch checked={!!form.cityRequired} onChange={(e) => setField('cityRequired', e.target.checked)} />} label="City Required" />
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Primary Color" value={form.primaryColor || '#1976d2'}
                  onChange={(e) => setField('primaryColor', e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Secondary Color" value={form.secondaryColor || '#dc004e'}
                  onChange={(e) => setField('secondaryColor', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Master Group Keys (comma-separated)"
                  helperText="e.g. transport_masters, food_masters, pricing"
                  value={(form.masterGroupKeys || []).join(',')}
                  onChange={(e) => setField('masterGroupKeys', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Setting Categories (comma-separated)"
                  value={(form.settingCategories || []).join(',')}
                  onChange={(e) => setField('settingCategories', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <Box>
              <Grid container spacing={1} mb={2}>
                <Grid item xs={3}><TextField fullWidth size="small" label="Label" value={menuDraft.label} onChange={(e) => setMenuDraft({ ...menuDraft, label: e.target.value })} /></Grid>
                <Grid item xs={3}><TextField fullWidth size="small" label="Path" value={menuDraft.path} onChange={(e) => setMenuDraft({ ...menuDraft, path: e.target.value })} /></Grid>
                <Grid item xs={3}><TextField select fullWidth size="small" label="Icon" value={menuDraft.icon} onChange={(e) => setMenuDraft({ ...menuDraft, icon: e.target.value })}>{ICON_OPTIONS.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}</TextField></Grid>
                <Grid item xs={2}><TextField fullWidth size="small" type="number" label="Order" value={menuDraft.sortOrder} onChange={(e) => setMenuDraft({ ...menuDraft, sortOrder: Number(e.target.value) })} /></Grid>
                <Grid item xs={1}><Button fullWidth variant="outlined" sx={{ height: '40px' }} onClick={() => {
                  if (!menuDraft.label || !menuDraft.path) return;
                  setField('menuItems', [...(form.menuItems || []), { ...menuDraft }]);
                  setMenuDraft({ label: '', path: '', icon: 'Build', sortOrder: 0 });
                }}>Add</Button></Grid>
              </Grid>
              {(form.menuItems || []).map((item, idx) => (
                <Chip key={idx} sx={{ m: 0.5 }}
                  label={`${item.label} → ${item.path}`}
                  onDelete={() => setField('menuItems', (form.menuItems || []).filter((_, i) => i !== idx))} />
              ))}
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Grid container spacing={1} mb={2}>
                <Grid item xs={5}><TextField fullWidth size="small" label="Name" value={subDraft.name} onChange={(e) => setSubDraft({ ...subDraft, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })} /></Grid>
                <Grid item xs={5}><TextField fullWidth size="small" label="Slug" value={subDraft.slug} onChange={(e) => setSubDraft({ ...subDraft, slug: e.target.value })} /></Grid>
                <Grid item xs={2}><Button fullWidth variant="outlined" sx={{ height: '40px' }} onClick={() => {
                  if (!subDraft.name || !subDraft.slug) return;
                  setField('subServiceTemplates', [...(form.subServiceTemplates || []), { ...subDraft }]);
                  setSubDraft({ name: '', slug: '' });
                }}>Add</Button></Grid>
              </Grid>
              {(form.subServiceTemplates || []).map((item, idx) => (
                <Chip key={idx} sx={{ m: 0.5 }} label={`${item.name} (${item.slug})`}
                  onDelete={() => setField('subServiceTemplates', (form.subServiceTemplates || []).filter((_, i) => i !== idx))} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate(form)} disabled={!form.code || !form.name}>
            {editing ? 'Save Changes' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
